import React, { useState, useEffect, useCallback } from 'react';
import { api } from './api/api';

// ADMIN-only: devices of the personal AmneziaWG VPN on the fobase server.
// Backend: /api/v1/vpn/devices (list / add / delete / {id}/config).

const fmtBytes = (b) => {
  if (!b) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i ? 1 : 0)} ${units[i]}`;
};

const fmtAgo = (unixSec) => {
  if (!unixSec) return 'never';
  const s = Math.floor(Date.now() / 1000) - unixSec;
  if (s < 180) return 'online now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
};

const fmtDate = (unixSec) => unixSec ? new Date(unixSec * 1000).toLocaleDateString() : '—';

const errText = (err, fallback) =>
  err.response?.status === 403 ? 'Access denied.' : (typeof err.response?.data === 'string' && err.response.data) || fallback;

const downloadConf = (name, config) => {
  const blob = new Blob([config], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fobase-vpn-${name.replace(/[^\p{L}\p{N}_-]+/gu, '_')}.conf`;
  a.click();
  URL.revokeObjectURL(url);
};

const ConfigModal = ({ device, onClose }) => (
  <div
    onClick={onClose}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
             display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
  >
    <div className="fb-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, width: '100%', margin: 0 }}>
      <div className="fb-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="fb-card-title">{device.name} · {device.ip}</span>
        <button type="button" className="fb-btn fb-btn-ghost fb-btn-sm" onClick={onClose}>✕</button>
      </div>
      <div style={{ padding: 16, textAlign: 'center' }}>
        <img
          alt="VPN config QR"
          src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(device.qrSvg)}`}
          style={{ width: '100%', maxWidth: 320, background: '#fff', borderRadius: 6 }}
        />
        <div style={{ fontSize: 12, color: 'var(--c-text-muted)', margin: '10px 0 14px' }}>
          Scan in the AmneziaVPN app, or import the .conf file. Don't share it — it's this device's key.
        </div>
        <button type="button" className="fb-btn fb-btn-primary fb-btn-sm" onClick={() => downloadConf(device.name, device.config)}>
          Download .conf
        </button>
      </div>
    </div>
  </div>
);

const VpnDevices = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/vpn/devices');
      setDevices(res.data || []);
      setError('');
    } catch (err) {
      setError(errText(err, 'Failed to load VPN devices.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const res = await api.post('/vpn/devices', { name: newName.trim() });
      setNewName('');
      setShown(res.data);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to add device.'));
    } finally {
      setBusy(false);
    }
  };

  const handleShow = async (d) => {
    try {
      const res = await api.get(`/vpn/devices/${d.id}/config`);
      setShown(res.data);
    } catch (err) {
      setError(errText(err, 'Failed to load config.'));
    }
  };

  const handleRemove = async (d) => {
    if (!window.confirm(`Revoke VPN access for "${d.name}"? The device will be disconnected immediately.`)) return;
    setBusy(true);
    try {
      await api.delete(`/vpn/devices/${d.id}`);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to remove device.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fb-page-header">
        <div className="fb-page-title">VPN</div>
        <div className="fb-page-sub">
          Personal AmneziaWG VPN on this server (Russian IP). One device — one config.
        </div>
      </div>

      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          className="fb-input"
          style={{ maxWidth: 280 }}
          placeholder="New device name, e.g. Tablet"
          maxLength={32}
          value={newName}
          onChange={e => setNewName(e.target.value)}
        />
        <button type="submit" className="fb-btn fb-btn-primary" disabled={busy || !newName.trim()}>
          {busy ? 'Working…' : 'Add device'}
        </button>
      </form>

      {error && <div style={{ color: 'var(--c-error)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div className="fb-card">
        <div className="fb-table-wrap">
          <table className="fb-table fb-table-sm">
            <thead>
              <tr><th>Device</th><th>VPN IP</th><th>Last seen</th><th>Traffic ↓ / ↑</th><th>Created</th><th></th></tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: 20 }}>Loading…</td></tr>
              )}
              {!loading && devices.map(d => (
                <tr key={d.id}>
                  <td style={{ fontWeight: 600 }}>{d.name}</td>
                  <td>{d.ip}</td>
                  <td style={{ color: fmtAgo(d.latestHandshake) === 'online now' ? 'var(--c-success, #2e9d5b)' : undefined }}>
                    {fmtAgo(d.latestHandshake)}
                  </td>
                  <td>{fmtBytes(d.txBytes)} / {fmtBytes(d.rxBytes)}</td>
                  <td>{fmtDate(d.createdAt)}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button type="button" className="fb-btn fb-btn-secondary fb-btn-sm" onClick={() => handleShow(d)}>QR / config</button>{' '}
                    <button type="button" className="fb-btn fb-btn-ghost fb-btn-sm" style={{ color: 'var(--c-error)' }} disabled={busy} onClick={() => handleRemove(d)}>Revoke</button>
                  </td>
                </tr>
              ))}
              {!loading && devices.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: 20 }}>No devices.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {shown && <ConfigModal device={shown} onClose={() => setShown(null)} />}
    </>
  );
};

export default VpnDevices;
